import React, { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db, ensureAnonymousAuth, ensureDurableAuthPersistence } from "../firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import {
  extractAppVariantFromOrgData,
  extractBusinessModeFromOrgData,
  isFieldOpsMode,
  modeLabel,
  persistBusinessContext,
  readStoredBusinessContext,
} from "../utils/businessMode";
import { readBinding, saveBinding, clearBinding, isBound } from "../utils/storeBinding";
import {
  getAuth,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";

/* ------------------ install-prompt helper (inline) ------------------ */
function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);

    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const inStandalone =
    (window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true;

  return {
    canInstall: !!deferred && !installed && !inStandalone,
    promptInstall: async () => {
      if (!deferred) return false;
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {
        /* ignore */
      }
      setDeferred(null);
      return true;
    },
  };
}


function getInstallContext() {
  const ua = (window.navigator.userAgent || "").toLowerCase();
  const platform = (window.navigator.platform || "").toLowerCase();
  const isStandalone =
    (window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true;
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (platform === "macintel" && window.navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(ua);

  return {
    isStandalone,
    isIOS,
    isAndroid,
    shouldShowBanner: !isStandalone,
  };
}

const fns = getFunctions(app, "us-central1");

/* ------------------ helpers & defaults ------------------ */

const LS_KEYS = ["orgId", "storeId", "storeName", "orgName", "deviceUid"];
const RESET_KEYS = [
  ...LS_KEYS,
  "storeLabel",
  "recoverySecret",
  "installParams",
  "linkedOrgId",
  "linkedStoreId",
  "linkedStoreName",
  "workspacePinVerified",
  "deviceLinked",
  "deviceLinkToken",
  "deviceSession",
  "deviceType",
  "businessMode",
  "appVariant",
  "managerId",
  "managerName",
  "managerStoreId",
  "managerStoreName",
  "staffId",
  "staffName",
  "staffRole",
  "managerSession",
  "cashup_manager_session",
  "session_manager",
  "staffSession",
  "cashup_staff_session",
  "session_staff",
  "cashup.session",
  "neo.lastLinkedBinding",
  "neo.lastLinkedAt",
  "neo.deviceRecoveryRequired",
];


async function signInDeviceWithCustomToken(customToken, expectedOrgId, expectedStoreId) {
  const auth = getAuth(app);
  await ensureDurableAuthPersistence();
  await signInWithCustomToken(auth, String(customToken));
  await auth.currentUser?.getIdToken(true);

  let lastClaims = null;

  for (let i = 0; i < 6; i += 1) {
    const tokenResult = await auth.currentUser?.getIdTokenResult(true);
    const claims = tokenResult?.claims || {};
    lastClaims = claims;

    console.log("[LinkDevice] token claims check", {
      uid: auth.currentUser?.uid || null,
      orgId: claims?.orgId || null,
      storeId: claims?.storeId || null,
      role: claims?.role || null,
      expectedOrgId,
      expectedStoreId,
      attempt: i + 1,
    });

    if (
      claims?.orgId === expectedOrgId &&
      (!expectedStoreId || !claims?.storeId || claims?.storeId === expectedStoreId)
    ) {
      if (auth.currentUser?.uid) {
        localStorage.setItem("deviceUid", String(auth.currentUser.uid));
      }
      return { auth, claims };
    }

    await new Promise((resolve) => setTimeout(resolve, 400));
  }

  throw new Error(
    "Device linked but auth claims are missing or stale. Please relink this device."
  );
}

function extractReturnedCustomToken(data = {}) {
  return (
    data?.customToken ||
    data?.token ||
    data?.authToken ||
    data?.deviceToken ||
    data?.result?.token ||
    data?.result?.customToken ||
    data?.payload?.token ||
    data?.payload?.customToken ||
    data?.data?.token ||
    data?.data?.customToken ||
    ""
  );
}

function safeJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

async function resetLinkedDeviceState() {
  try {
    RESET_KEYS.forEach((k) => localStorage.removeItem(k));
    clearBinding();
  } catch {
    /* ignore */
  }

  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }

  try {
    const auth = getAuth(app);
    if (auth.currentUser) {
      await signOut(auth);
    }
  } catch {
    /* ignore */
  }

  try {
    if (window.caches && typeof window.caches.keys === "function") {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map((key) => window.caches.delete(key)));
    }
  } catch {
    /* ignore */
  }
}

function normalizeMode(mode) {
  const v = String(mode || "").toLowerCase();
  if (v.includes("cover")) return "cover";
  if (v.includes("center")) return "image-centered";
  if (v === "background-cover") return "cover";
  if (v === "background") return "image-centered";
  return "image-centered";
}

const DEF_TEXT = {
  x: 0.5,
  y: 0.9,
  size: 20,
  weight: 800,
  italic: false,
  color: "#e8edf7",
  shadow: { blur: 0, x: 0, y: 0, color: "rgba(0,0,0,0.6)" },
};
const DEF_SUB = { ...DEF_TEXT, size: 13, weight: 500, y: 0.95 };
const DEF_IMG = { x: 0.5, y: 0.5, scale: 1, rotate: 0 };

function mergeSplashFromDoc(brandDoc = {}) {
  const b = brandDoc || {};
  const s = b.splash || {};
  return {
    mode: normalizeMode(s.mode || "image-centered"),
    durationMs:
      s.durationMs != null
        ? Number(s.durationMs)
        : (Number(s.durationSec) || 5) * 1000,
    imageUrl: s.imageUrl || "/logo512.png",
    image: { ...DEF_IMG, ...(s.image || {}) },
    title: s.title || "",
    subtitle: s.subtitle || "",
    titleStyle: { ...DEF_TEXT, ...(s.titleStyle || {}) },
    subtitleStyle: { ...DEF_SUB, ...(s.subtitleStyle || {}) },
    colors: {
      bg: b.bg ?? b.brand?.bg ?? "#0b1220",
      accent: b.accent ?? b.brand?.accent ?? "#14b8a6",
      text: b.text ?? b.brand?.text ?? "#e8edf7",
    },
  };
}

async function tryResolveStoreName(orgId, storeId) {
  if (!orgId || !storeId) return "";

  const tryDoc = async (pathArr) => {
    try {
      const snap = await getDoc(doc(db, ...pathArr));
      return snap.exists() ? snap.data() : null;
    } catch {
      return null;
    }
  };

  const sources = [
    ["orgs", orgId, "cashup_stores", storeId],
    ["cashup_stores", storeId],
    ["orgs", orgId, "stores", storeId],
  ];

  for (const p of sources) {
    const data = await tryDoc(p);
    if (!data) continue;
    if (data.name) return String(data.name);
    if (data.storeName) return String(data.storeName);
    if (data.label) return String(data.label);
  }
  return "";
}


function hasLinkedNeoDevice() {
  try {
    return isBound();
  } catch {
    return false;
  }
}

function installParamsMatchExistingBinding(search = window.location.search) {
  try {
    const sp = new URLSearchParams(search || "");
    const first = (...keys) => keys.map((k) => sp.get(k)).find(Boolean) || "";
    const linkOrgId = first("org", "orgId", "ORG", "ORGID");
    const linkStoreId = first("store", "storeId", "STORE", "STOREID");
    const binding = readBinding();

    if (!binding?.orgId || !binding?.storeId) return false;
    if (linkOrgId && String(linkOrgId) !== String(binding.orgId)) return false;
    if (linkStoreId && String(linkStoreId) !== String(binding.storeId)) return false;

    // If the installed mobile PWA was added from /link?... it can reopen with
    // the original install params every time. Once the same device is already
    // linked, those params are no longer a new link attempt; they are just the
    // saved launch URL. Bounce into Neo instead of showing Link Device again.
    return !!(linkOrgId || linkStoreId);
  } catch {
    return false;
  }
}

function hasFreshInstallLinkParams(search = window.location.search) {
  const sp = new URLSearchParams(search || "");
  return !!(
    sp.get("org") ||
    sp.get("orgId") ||
    sp.get("store") ||
    sp.get("storeId") ||
    sp.get("token") ||
    sp.get("firstStore")
  );
}

function hasResetQueryParam(search = window.location.search) {
  try {
    const sp = new URLSearchParams(search || "");
    return !!(
      sp.get("reset") ||
      sp.get("relink") ||
      sp.get("killed") ||
      sp.get("unlinked") ||
      false
    );
  } catch {
    return false;
  }
}

function consumeAllowedResetFlag() {
  try {
    const allowed = sessionStorage.getItem("neo.allowDeviceReset") === "1";
    sessionStorage.removeItem("neo.allowDeviceReset");
    return allowed;
  } catch {
    return false;
  }
}

function isExplicitDeviceReset(search = window.location.search) {
  // Do not trust /link?reset=1 by itself. Android/Chrome shortcuts can preserve
  // the exact URL used during reset/install, causing every relaunch to wipe the
  // just-linked device. Only the in-app Reset button sets this one-shot flag.
  return hasResetQueryParam(search) && consumeAllowedResetFlag();
}

function hasResetFlagInSearch(search = window.location.search) {
  return hasResetQueryParam(search);
}

function clearResetQueryFromAddressBar() {
  try {
    if (window.location.pathname === "/link" && hasResetFlagInSearch(window.location.search)) {
      window.history.replaceState({ lockLinkPage: true }, "", "/link");
    }
  } catch {
    /* ignore */
  }
}

/* ------------------ main component ------------------ */

export default function LinkDevice() {
  console.log("✅ LINKDEVICE FINAL VERSION LOADED");
  // add/remove body.linking for dark gradient & safe-area padding
  useEffect(() => {
    document.body.classList.add("linking");
    return () => document.body.classList.remove("linking");
  }, []);

  useEffect(() => {
    let active = true;
    const originalPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const hadResetQuery = hasResetQueryParam(window.location.search);
    const explicitReset = isExplicitDeviceReset(window.location.search);
    if (hadResetQuery) clearResetQueryFromAddressBar();
    if (explicitReset) {
      try {
        // Clear stale link params synchronously before this page reads saved install data.
        // Keep a persistent relink lock so stale Firebase custom claims cannot silently
        // rebuild the old binding before a fresh PIN is successfully consumed.
        RESET_KEYS.forEach((k) => localStorage.removeItem(k));
        clearBinding();
        localStorage.setItem("neo.relinkRequired", "1");
        sessionStorage.clear();
      } catch {
        /* ignore */
      }
    }
    const currentPath = hadResetQuery ? "/link" : originalPath;
    const hasInstallParams = !explicitReset && hasFreshInstallLinkParams(window.location.search);

    // Important mobile PWA fix:
    // iOS/Android can save the exact URL used during Add-to-Home-Screen. If the
    // device was installed from /link?... it may reopen on that same /link URL
    // after the app is fully closed. When this browser is already linked to the
    // same org/store, treat those params as the saved launch URL, not a new link.
    const relinkRequired = (() => {
      try { return localStorage.getItem("neo.relinkRequired") === "1"; } catch { return false; }
    })();

    if (!relinkRequired && hasLinkedNeoDevice() && !explicitReset && (!hasInstallParams || installParamsMatchExistingBinding(window.location.search))) {
      window.location.replace("/");
      return () => {
        active = false;
      };
    }

    const holdOnLinkPage = () => {
      try {
        window.history.pushState({ lockLinkPage: true }, "", currentPath);
      } catch {
        /* ignore */
      }
    };

    const prepareLinkPage = async () => {
      // Only clear stored device state for a real reset/relink, or for a plain
      // /link visit with no install params and no binding. Do NOT clear when
      // mobile reopens an installed /link?... launch URL, because Firebase auth
      // or mirrored storage may still be hydrating and can heal the binding.
      if (explicitReset || (!hasInstallParams && !hasLinkedNeoDevice())) {
        await resetLinkedDeviceState();
      }

      if (!active) return;

      try {
        window.history.replaceState({ lockLinkPage: true }, "", currentPath);
        holdOnLinkPage();
      } catch {
        /* ignore */
      }
    };

    const onPopState = () => {
      holdOnLinkPage();
    };

    const onPageShow = (event) => {
      if (event.persisted) {
        window.location.replace(currentPath);
      }
    };

    prepareLinkPage();
    window.addEventListener("popstate", onPopState);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      active = false;
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  const { canInstall, promptInstall } = useInstallPrompt();
  const installCtx = useMemo(() => getInstallContext(), []);
  const [installDismissed, setInstallDismissed] = useState(false);

  // URL params (accept multiple naming conventions + persist for PWA relaunch)
  const params = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    const first = (...keys) =>
      keys.map((k) => sp.get(k)).find((v) => v != null && v !== "") || "";

    const parsed = {
      orgId: first("org", "orgId", "ORG", "ORGID"),
      storeId: first("store", "storeId", "STORE", "STOREID"),
      token: first("token", "firstStore", "TOKEN", "FIRSTSTORE"),
      exp: Number(first("exp")) || 0,
      orgName: first("orgName", "ORGNAME"),
      storeName: first(
        "storeName",
        "STORENAME",
        "storeLabel",
        "STORELABEL"
      ),
      mode: first("mode", "businessMode", "MODE", "BUSINESSMODE"),
      appVariant: first("appVariant", "variant", "APPVARIANT", "VARIANT"),
    };

    // Persist install params so A2HS (iOS/Android) can relaunch without query string.
    // On /link?reset=1, never hydrate from the old installParams. Mobile PWAs can
    // render once before effects run, so reading stale params here caused the app
    // to bounce back into an old/blank binding after reset.
    try {
      const resetLaunch = hasResetFlagInSearch(window.location.search);
      const hasAny = !!(parsed.orgId || parsed.storeId || parsed.token);
      if (resetLaunch) {
        localStorage.removeItem("installParams");
      } else if (hasAny) {
        localStorage.setItem("installParams", JSON.stringify(parsed));
      } else {
        const saved = localStorage.getItem("installParams");
        if (saved) {
          const fromSaved = JSON.parse(saved);
          parsed.orgId = parsed.orgId || fromSaved.orgId || "";
          parsed.storeId = parsed.storeId || fromSaved.storeId || "";
          parsed.token = parsed.token || fromSaved.token || "";
          parsed.exp = parsed.exp || fromSaved.exp || 0;
          parsed.orgName = parsed.orgName || fromSaved.orgName || "";
          parsed.storeName = parsed.storeName || fromSaved.storeName || "";
          parsed.mode = parsed.mode || fromSaved.businessMode || fromSaved.mode || "";
          parsed.appVariant = parsed.appVariant || fromSaved.appVariant || "";
        }
      }
    } catch {
      /* ignore */
    }

    return parsed;
  }, []);

  const storedBusiness = readStoredBusinessContext();
  const initialLinkMode = params.mode || null;
  const [businessMode, setBusinessMode] = useState(initialLinkMode);
  const [orgName, setOrgName] = useState("");
  const [storeName, setStoreName] = useState("");
  const [brand, setBrand] = useState(null);

  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    onOk: null,
  });
  const [showSplash, setShowSplash] = useState(false);
  const [splashCfg, setSplashCfg] = useState(null);

  const [manualLink, setManualLink] = useState("");
  const [manualError, setManualError] = useState("");

  useEffect(() => {
    if (!params.orgId) return;
    const modeFromLink = params.mode || businessMode || null;
    const variantFromLink =
      params.appVariant ||
      (isFieldOpsMode(modeFromLink) ? "personal_device" : "shared_device");
    const persisted = persistBusinessContext({
      businessMode: modeFromLink,
      appVariant: variantFromLink,
    });
    setBusinessMode(persisted.businessMode);
  }, [params.orgId, params.mode, params.appVariant]);

  const handleManualLink = () => {
    try {
      setManualError("");
      const url = String(manualLink || "").trim();
      if (!url) {
        setManualError("Paste an install link first.");
        return;
      }

      // Allow pasting the full URL OR just the query string
      const hasProtocol = /^https?:\/\//i.test(url);
      const u = hasProtocol
        ? new URL(url)
        : new URL(
            window.location.origin +
              "/link" +
              (url.startsWith("?") ? url : "?" + url)
          );

      // Force it to open LinkDevice with the same params
      window.location.assign("/link" + u.search);
    } catch (e) {
      console.error("Invalid install link:", e);
      setManualError(
        "That install link looks invalid. Paste the full link from Admin/Email."
      );
    }
  };

  // Load org/store labels + branding
  useEffect(() => {
    if (!params.orgId) return;

    (async () => {
      // Prefer names passed via URL (works before sign-in without Firestore reads)
      if (params.orgName) setOrgName(params.orgName);
      if (params.storeName) setStoreName(params.storeName);

      // org name (best effort)
      try {
        const os = await getDoc(doc(db, "orgs", params.orgId));
        const od = os.exists() ? os.data() : {};
        const fallbackMode = params.mode || businessMode || null;
        const resolvedMode = extractBusinessModeFromOrgData(od) || fallbackMode;
        const resolvedVariant = extractAppVariantFromOrgData(od, resolvedMode || fallbackMode);
        persistBusinessContext({ businessMode: resolvedMode, appVariant: resolvedVariant });
        setBusinessMode(resolvedMode);
        setOrgName(
          String(
            od.name ||
              od.orgName ||
              od.label ||
              params.orgName ||
              params.orgId
          )
        );
      } catch {
        setOrgName(params.orgName || params.orgId);
      }

      const tryDoc2 = async (pathArr) => {
        try {
          const snap = await getDoc(doc(db, ...pathArr));
          return snap.exists() ? snap.data() : null;
        } catch {
          return null;
        }
      };

      // store name (best effort pre-auth)
      if (params.storeId) {
        const s1 = await tryDoc2([
          "orgs",
          params.orgId,
          "cashup_stores",
          params.storeId,
        ]);
        const s2 = s1 || (await tryDoc2(["cashup_stores", params.storeId]));
        const sName = s2?.name || s2?.storeName || s2?.label || "";
        if (sName) setStoreName(String(sName));
      }

      // Branding sources (best effort)
      const sources = [
        ["orgs", params.orgId, "branding", "global"],
        ["orgs", params.orgId, "cashup_stores", params.storeId || ""],
        ["cashup_stores", params.storeId || ""],
      ];
      for (const p of sources) {
        if (p.includes("")) continue;
        const snap = await tryDoc2(p);
        if (snap) {
          setBrand(snap);
          break;
        }
      }
    })();
  }, [params.orgId, params.storeId, params.orgName, params.storeName, params.mode, params.appVariant]);

// Submit PIN
async function onSubmit(e) {
  e?.preventDefault?.();
  setErrorMsg("");

  const pinTrim = String(pin || "").trim();
  if (!pinTrim) {
    setErrorMsg("Please enter the PIN from Admin → Download.");
    return;
  }

  // ✅ This is exactly what we compare to Firestore installPins/{token}
  // generate friendly device label
  const ua = navigator.userAgent || "";
  let deviceLabel = "Device";
  if (/android/i.test(ua)) deviceLabel = "Android Device";
  else if (/iphone|ipad/i.test(ua)) deviceLabel = "iOS Device";
  else if (/windows/i.test(ua)) deviceLabel = "Windows Device";
  else if (/mac/i.test(ua)) deviceLabel = "Mac Device";

  const payload = {
    deviceLabel,
    pin: pinTrim,
    orgId: String(params.orgId || "").trim(),
    storeId: String(params.storeId || "").trim(),
    token: String(params.token || "").trim(), // MUST equal Firestore doc id
  };

  // ✅ ALWAYS log payload before calling the function
  console.groupCollapsed("[LinkDevice] consumeStoreInstallPin");
  console.log("payload:", payload);
  console.log("URL:", window.location.href);
  console.groupEnd();

setSubmitting(true);
try {

  const fns = getFunctions(app, "us-central1");
  const consumeStoreInstallPin = httpsCallable(fns, "consumeStoreInstallPin");

    const res = await consumeStoreInstallPin(payload);
    console.log("[LinkDevice] response:", res);

    const data = res?.data || {};
    console.log("[LinkDevice] data =>", data);

    // If backend returns a custom token, sign in immediately and force-refresh claims.
    // Storage / Firestore rules depend on orgId + storeId custom claims being present.
    let signedInWithCustomToken = false;

    console.log("🔥 FULL FUNCTION RESPONSE:", res);
    console.log("🔥 FULL FUNCTION RESPONSE JSON:", safeJson(res));
    console.log("🔥 DATA:", data);
    console.log("🔥 DATA JSON:", safeJson(data));

    const returnedCustomToken = extractReturnedCustomToken(data);
    console.log("🔑 Extracted Token:", returnedCustomToken ? `${String(returnedCustomToken).slice(0, 18)}...` : "(empty)");

    if (!data || data.ok === false || data.status === "error") {
      throw new Error(data?.message || "Invalid PIN. Please try again.");
    }

    const orgId = data.orgId || data.org || data?.result?.orgId || data?.payload?.orgId || params.orgId || "";
    const storeId = data.storeId || data.store || data?.result?.storeId || data?.payload?.storeId || params.storeId || "";
    if (!orgId || !storeId) throw new Error("Invalid server response (missing org/store).");

    if (returnedCustomToken && String(returnedCustomToken).length >= 20) {
      try {
        await signInDeviceWithCustomToken(returnedCustomToken, orgId, storeId);
        signedInWithCustomToken = true;
      } catch (e) {
        console.error("[LinkDevice] custom-token auth failed =>", e);
        throw e;
      }
    } else {
      // Older/current Cloud Function versions may return ok/linked without a custom token.
      // Do not block the device link in that case; keep the local device binding and let
      // the app continue with its normal anonymous-auth session.
      console.warn("[LinkDevice] no custom token returned; continuing with local linked state", data);
      try {
        const anonUser = await ensureAnonymousAuth();
        if (anonUser?.uid && !data.deviceUid) {
          localStorage.setItem("deviceUid", String(anonUser.uid));
        }
      } catch (anonErr) {
        console.warn("[LinkDevice] anonymous auth fallback skipped", anonErr);
      }
    }

    let resolvedPostLinkMode = params.mode || businessMode || null;
    let resolvedPostLinkVariant = isFieldOpsMode(resolvedPostLinkMode) ? "personal_device" : "shared_device";

    try {
      const orgSnap = await getDoc(doc(db, "orgs", orgId));
      if (orgSnap.exists()) {
        const orgData = orgSnap.data() || {};
        const firestoreMode = extractBusinessModeFromOrgData(orgData);
        resolvedPostLinkMode = firestoreMode || resolvedPostLinkMode;
        resolvedPostLinkVariant = extractAppVariantFromOrgData(orgData, resolvedPostLinkMode);
      }
    } catch (modeErr) {
      console.warn("Business mode lookup skipped", modeErr);
    }

    console.log("[LinkDevice] device auth ready", {
      orgId,
      storeId,
      signedInWithCustomToken,
    });

    const persistedBusiness = persistBusinessContext({
      businessMode: resolvedPostLinkMode,
      appVariant: resolvedPostLinkVariant,
    });
    resolvedPostLinkMode = persistedBusiness.businessMode;
    resolvedPostLinkVariant = persistedBusiness.appVariant;
    setBusinessMode(resolvedPostLinkMode);

    // Avoid protected Firestore reads until the custom-token session is definitely ready.
    // The install link already contains enough display information for a successful link.
    let resolvedName =
      storeName ||
      data.storeName ||
      data.storeLabel ||
      params.storeName ||
      params.storeId ||
      "";

    if (signedInWithCustomToken && !resolvedName) {
      try {
        resolvedName = (await tryResolveStoreName(orgId, storeId)) || resolvedName;
      } catch (e) {
        console.warn("Store-name lookup skipped", e);
      }
    }

    // Persist the linked device in multiple first-party storage layers.
    // This protects installed mobile PWAs that relaunch on /link and read localStorage late/stale.
    LS_KEYS.forEach((k) => localStorage.removeItem(k));
    const resolvedOrgName =
      orgName ||
      data.orgName ||
      data.orgLabel ||
      params.orgName ||
      readBinding().orgName ||
      localStorage.getItem("orgName") ||
      "";

    const linkedDeviceUid = data.deviceUid || localStorage.getItem("deviceUid") || "";
    const finalBinding = saveBinding({
      orgId,
      storeId,
      deviceUid: linkedDeviceUid ? String(linkedDeviceUid) : "",
      orgName: resolvedOrgName || orgId,
      storeName: resolvedName || storeId,
      deviceType: isFieldOpsMode(resolvedPostLinkMode) ? "neo_security" : "neo_kiosk",
      businessMode: resolvedPostLinkMode || "",
      appVariant: resolvedPostLinkVariant || "",
      recoverySecret: String(data.recoverySecret || data.deviceRecoverySecret || ""),
    });

    // Only a verified fresh link may release the reset/relink lock.
    try {
      localStorage.removeItem("neo.relinkRequired");
      localStorage.removeItem("neo.relinkReason");
    } catch {}


    // Linking/relinking establishes a new store security boundary.
    // Remove any staff or manager session inherited from the previously linked
    // store before the user enters Clock In / Workspace / Manager Login.
    try {
      [
        "staffSession",
        "cashup_staff_session",
        "session_staff",
        "cashup.session",
        "staffName",
        "cashup_staff_name",
        "staffRole",
        "staffId",
        "mgrSession",
        "managerSession",
        "cashup_manager_session",
        "session_manager",
        "managerId",
        "managerName",
        "isManager",
        "workspacePinVerified",
      ].forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
    } catch {}

    try {
      // A valid PIN has now been consumed and the fresh device binding exists.
      // Only now may auth claims/local storage be trusted again.
      localStorage.removeItem("neo.relinkRequired");

      // Backward-compatible markers for older screens/guards and a quick mobile
      // recovery marker if localStorage is briefly unavailable on PWA relaunch.
      localStorage.setItem("deviceLinked", "true");
      localStorage.setItem("linkedOrgId", String(orgId));
      localStorage.setItem("linkedStoreId", String(storeId));
      localStorage.setItem("linkedStoreName", String(resolvedName || storeId));
      localStorage.setItem("neo.lastLinkedBinding", JSON.stringify(finalBinding));
      localStorage.setItem("neo.lastLinkedAt", String(Date.now()));
    } catch {
      /* ignore */
    }

    setOrgName(resolvedOrgName || orgId);
    setStoreName(resolvedName);

    try {
      const savedInstall = JSON.parse(localStorage.getItem("installParams") || "{}");
      localStorage.setItem(
        "installParams",
        JSON.stringify({
          ...savedInstall,
          orgId,
          storeId,
          token: String(params.token || savedInstall.token || ""),
          exp: Number(params.exp || savedInstall.exp || 0),
          orgName: resolvedOrgName || savedInstall.orgName || "",
          storeName: resolvedName || savedInstall.storeName || "",
          mode: resolvedPostLinkMode || "",
          businessMode: resolvedPostLinkMode || "",
          appVariant: resolvedPostLinkVariant || "",
        })
      );
    } catch {
      /* ignore */
    }

    // Reload latest branding only after the kiosk auth session is ready.
    let fresh = brand;
    if (signedInWithCustomToken) {
      try {
        fresh = await loadBranding(orgId, storeId);
      } catch (e) {
        console.warn("Branding reload skipped", e);
      }
    }

    // Confirm, then splash, then enter
    openModal(
      "Device linked",
      `This device is now linked to ${resolvedOrgName ? `${resolvedOrgName} — ` : ""}${resolvedName || "the store"}.`,
      () => {
        closeModal();
        playSplashThenEnter(fresh, resolvedPostLinkMode);
      }
    );
  } catch (err) {
    console.error("[LinkDevice] consumeStoreInstallPin error =>", err);

    // Helpful for Firebase callable errors
    if (err?.code) console.error("[LinkDevice] firebase error code:", err.code);

    const msg =
      err?.message ||
      (typeof err === "string" ? err : "") ||
      "Could not verify PIN.";

    setErrorMsg(msg);
    openModal("Invalid PIN", msg);
  } finally {
    setSubmitting(false);
  }
}

  async function loadBranding(orgId, storeId) {
    const tryDoc3 = async (pathArr) => {
      try {
        const snap = await getDoc(doc(db, ...pathArr));
        return snap.exists() ? snap.data() : null;
      } catch {
        return null;
      }
    };
    const sources = [
      ["orgs", orgId, "branding", "global"],
      ["orgs", orgId, "cashup_stores", storeId],
      ["cashup_stores", storeId],
    ];
    for (const p of sources) {
      const data = await tryDoc3(p);
      if (data) return data;
    }
    return brand;
  }

  function playSplashThenEnter(branding, nextMode = businessMode) {
    // strip query so refresh doesn’t keep the /link params
    window.history.replaceState({}, "", `${window.location.origin}/`);

    const cfg = mergeSplashFromDoc(branding || {});
    setSplashCfg(cfg);
    setShowSplash(true);

    setTimeout(() => {
      setShowSplash(false);
      window.location.replace("/");
    }, cfg.durationMs || 1200);
  }

  function openModal(title, message, onOk) {
    setModal({ open: true, title, message, onOk: onOk || null });
  }
  function closeModal() {
    setModal((m) => ({ ...m, open: false }));
  }

  /* ------------------ render ------------------ */

  const s = styles();
  const displayStore = storeName || params.storeName || params.storeId || "Waiting for store";
  const displayOrg = orgName || params.orgName || params.orgId || "Ready to link";

  return (
    <>
      <div style={s.shell}>
        <div style={s.grid} />
        <div style={s.orbOne} />
        <div style={s.orbTwo} />

        <div style={s.card}>
          <div style={s.cardGlow} />

          <div style={s.topRow}>
            <div style={s.brandStack}>
              <div style={s.brandBadge}>NEO DEVICE</div>
              {canInstall && (
                <button type="button" style={s.installUnderBadge} onClick={promptInstall}>
                  Install Neo
                </button>
              )}
            </div>
            <div style={s.statusPill}>
              <span style={s.statusDot} />
              Secure setup
            </div>
          </div>

          <div style={s.logoWrap}>
            <img src="/logo512.png" alt="Neo" style={s.logoImg} />
          </div>

          <div style={s.layerText}>Live Operations</div>

          <div style={s.header}>
            <h1 style={s.title}>Link Neo Device</h1>
            <p style={s.subtitle}>
              Connect this browser to the correct store workspace.
            </p>

            <div style={s.workspaceCard}>
              <div>
                <div style={s.workspaceLabel}>Store</div>
                <div style={s.workspaceName}>{displayStore}</div>
              </div>
              <div style={s.workspaceDivider} />
              <div>
                <div style={s.workspaceLabel}>Organisation</div>
                <div style={s.workspaceName}>{displayOrg}</div>
              </div>
            </div>
          </div>

          {isFieldOpsMode(businessMode) && (
            <div style={s.modeBanner}>
              <div style={s.modeBannerTitle}>Field-services mode detected</div>
              <div style={s.modeBannerText}>
                This organization is configured for {modeLabel(businessMode)}.
                After linking, Neo will open the correct field workspace.
              </div>
            </div>
          )}

          {installCtx.shouldShowBanner && !installDismissed && (
            <div style={s.installBanner}>
              <div style={s.installBannerTop}>
                <div>
                  <div style={s.installBannerTitle}>
                    Install Neo for the best device experience
                  </div>
                  <div style={s.installBannerText}>
                    {installCtx.isIOS
                      ? "On iPhone or iPad, open Safari, tap Share, then Add to Home Screen."
                      : installCtx.isAndroid
                        ? canInstall
                          ? "Tap below to install Neo on this Android device."
                          : "Use the Chrome menu and choose Install app or Add to Home screen."
                        : "Install Neo from your browser menu or save it to your home screen."}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setInstallDismissed(true)}
                  style={s.installBannerClose}
                  aria-label="Dismiss install help"
                >
                  ×
                </button>
              </div>

              {installCtx.isIOS ? (
                <div style={s.installSteps}>
                  <span style={s.stepChip}>1. Open in Safari</span>
                  <span style={s.stepChip}>2. Tap Share</span>
                  <span style={s.stepChip}>3. Add to Home Screen</span>
                </div>
              ) : canInstall ? (
                <button type="button" onClick={promptInstall} style={s.installCta}>
                  Install Neo
                </button>
              ) : installCtx.isAndroid ? (
                <div style={s.installHint}>Chrome menu → Install app</div>
              ) : (
                <div style={s.installHint}>
                  Use your browser menu to install or save this app.
                </div>
              )}
            </div>
          )}

          <form onSubmit={onSubmit} style={s.form}>
            <label style={s.label}>Linking PIN</label>
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              value={pin}
              onChange={(e) => {
                setErrorMsg("");
                setPin(e.target.value.replace(/\D/g, ""));
              }}
              placeholder="Enter linking PIN"
              autoFocus
              style={s.pin}
              aria-label="Setup PIN"
            />

            {!!errorMsg && <div style={s.err}>{errorMsg}</div>}

            <button
              type="submit"
              disabled={submitting || !pin.trim()}
              style={{
                ...s.btn,
                ...(submitting || !pin.trim() ? s.btnDisabled : {}),
              }}
            >
              {submitting ? "Verifying…" : "Link Neo Device"}
            </button>

            {!!params.exp && (
              <div style={s.expText}>
                PIN expires in <ExpCountdown expMs={params.exp} />
              </div>
            )}
          </form>

          <div style={s.divider} />

          <div style={s.manualPanel}>
            <div style={s.manualTitle}>Already have a Neo install link?</div>
            <div style={s.manualText}>
              Paste the full install link from Admin if the PIN link was opened on another browser.
            </div>

            <input
              type="text"
              placeholder="Paste Neo install link here..."
              value={manualLink}
              onChange={(e) => setManualLink(e.target.value)}
              style={s.manualInput}
            />

            <button type="button" onClick={handleManualLink} style={s.manualBtn}>
              Link This Neo Device
            </button>

            {manualError && <div style={s.manualError}>{manualError}</div>}
          </div>

          <div style={s.featureGrid}>
            <div style={s.featureCard}>
              <strong>Secure</strong>
              <span>Device-bound access</span>
            </div>
            <div style={s.featureCard}>
              <strong>Live</strong>
              <span>Store workspace</span>
            </div>
          </div>

          <button onClick={() => window.close()} style={s.linkBtn}>
            Exit
          </button>

          <small style={s.helpText}>
            One-time setup for this browser. After linking, Neo opens directly into the correct live workspace.
          </small>

          {modal.open && <Modal {...modal} onClose={closeModal} />}
        </div>
      </div>

      {showSplash && splashCfg && <Splash cfg={splashCfg} />}
    </>
  );

}

/* ------------------ small pieces ------------------ */

function ExpCountdown({ expMs }) {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    const tick = () => {
      const left = Math.max(0, expMs - Date.now());
      const mm = Math.floor(left / 60000);
      const ss = Math.floor((left % 60000) / 1000);
      setTxt(`${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expMs]);
  return <strong>{txt}</strong>;
}

function Modal({ open, title, message, onOk, onClose }) {
  if (!open) return null;
  const s = styles();
  return (
    <div style={s.modalWrap}>
      <div style={s.modalBox}>
        <div style={s.modalHeader}>
          <div>{title}</div>
          <button onClick={onClose} style={s.closeBtn}>
            ×
          </button>
        </div>
        <div style={s.modalBody}>{message}</div>
        <div
          style={{ ...s.modalFooter, display: "flex", justifyContent: "center" }}
        >
          <button onClick={onOk || onClose} style={{ ...s.btn, minWidth: 140 }}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function Splash({ cfg }) {
  const s = splashStyles(cfg);
  return (
    <div style={s.wrap}>
      <div style={s.bg} />
      {cfg?.imageUrl ? (
        cfg.mode === "cover" ? (
          <img alt="" src={cfg.imageUrl} style={s.cover} />
        ) : (
          <img
            alt=""
            src={cfg.imageUrl}
            style={{
              position: "absolute",
              left: `${(cfg.image?.x ?? 0.5) * 100}%`,
              top: `${(cfg.image?.y ?? 0.5) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${cfg.image?.rotate ?? 0}deg) scale(${cfg.image?.scale ?? 1})`,
              width: "min(520px, 70vw)",
              borderRadius: 0,
              boxShadow: "none",
              filter: "drop-shadow(0 0 30px rgba(0, 210, 255, .28))",
            }}
          />
        )
      ) : null}

      {!!cfg?.title && (
        <div
          style={{
            position: "absolute",
            left: `${(cfg.titleStyle?.x ?? 0.5) * 100}%`,
            top: `${(cfg.titleStyle?.y ?? 0.9) * 100}%`,
            transform: "translate(-50%, -50%)",
            fontWeight: cfg.titleStyle?.weight ?? 800,
            fontSize: cfg.titleStyle?.size ?? 20,
            fontStyle: cfg.titleStyle?.italic ? "italic" : "normal",
            color: cfg.titleStyle?.color || cfg.colors?.text || "#e8edf7",
            textShadow:
              (cfg.titleStyle?.shadow?.blur || 0) > 0
                ? `${cfg.titleStyle.shadow.x || 0}px ${cfg.titleStyle.shadow.y || 0}px ${cfg.titleStyle.shadow.blur || 0}px ${cfg.titleStyle.shadow.color || "rgba(0,0,0,.6)"}`
                : "none",
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
        >
          {cfg.title}
        </div>
      )}

      {!!cfg?.subtitle && (
        <div
          style={{
            position: "absolute",
            left: `${(cfg.subtitleStyle?.x ?? 0.5) * 100}%`,
            top: `${(cfg.subtitleStyle?.y ?? 0.95) * 100}%`,
            transform: "translate(-50%, -50%)",
            fontWeight: cfg.subtitleStyle?.weight ?? 500,
            fontSize: cfg.subtitleStyle?.size ?? 13,
            fontStyle: cfg.subtitleStyle?.italic ? "italic" : "normal",
            color: cfg.subtitleStyle?.color || cfg.colors?.text || "#e8edf7",
            textShadow:
              (cfg.subtitleStyle?.shadow?.blur || 0) > 0
                ? `${cfg.subtitleStyle.shadow.x || 0}px ${cfg.subtitleStyle.shadow.y || 0}px ${cfg.subtitleStyle.shadow.blur || 0}px ${cfg.subtitleStyle.shadow.color || "rgba(0,0,0,.6)"}`
                : "none",
            opacity: 0.9,
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
        >
          {cfg.subtitle}
        </div>
      )}
    </div>
  );
}

/* ------------------ styles ------------------ */

function styles() {
  return {
    shell: {
      position: "relative",
      display: "grid",
      placeItems: "start center",
      width: "100%",
      minHeight: "100dvh",
      padding: "96px 16px 56px",
      overflowY: "auto",
      overflowX: "hidden",
      background:
        "radial-gradient(900px 520px at 12% 8%, rgba(0,210,255,0.18), transparent 42%)," +
        "radial-gradient(760px 520px at 90% 16%, rgba(49,130,255,0.20), transparent 44%)," +
        "radial-gradient(620px 420px at 70% 88%, rgba(20,199,126,0.10), transparent 45%)," +
        "linear-gradient(135deg,#04111f 0%,#071a33 48%,#050b16 100%)",
      color: "#e5e7eb",
      fontFamily:
        "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,'Apple Color Emoji','Segoe UI Emoji'",
    },
    grid: {
      position: "fixed",
      inset: 0,
      pointerEvents: "none",
      backgroundImage:
        "linear-gradient(rgba(125,211,252,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(125,211,252,0.055) 1px, transparent 1px)",
      backgroundSize: "76px 76px",
      maskImage: "linear-gradient(to bottom, rgba(0,0,0,.88), rgba(0,0,0,.22))",
    },
    orbOne: {
      position: "fixed",
      width: 240,
      height: 240,
      left: "18%",
      top: "18%",
      borderRadius: "50%",
      background: "rgba(0,210,255,0.08)",
      filter: "blur(34px)",
      pointerEvents: "none",
    },
    orbTwo: {
      position: "fixed",
      width: 280,
      height: 280,
      right: "16%",
      bottom: "16%",
      borderRadius: "50%",
      background: "rgba(49,130,255,0.10)",
      filter: "blur(42px)",
      pointerEvents: "none",
    },
    install: {
      position: "fixed",
      top: "max(12px, env(safe-area-inset-top))",
      right: "max(12px, env(safe-area-inset-right))",
      zIndex: 40,
      background: "linear-gradient(90deg,#21d4fd,#3182ff)",
      color: "#fff",
      fontWeight: 900,
      border: 0,
      borderRadius: 999,
      padding: "10px 15px",
      boxShadow: "0 12px 30px rgba(33,212,253,.22)",
      cursor: "pointer",
    },
    card: {
      position: "relative",
      marginTop: 0,
      zIndex: 1,
      width: "min(560px, 94vw)",
      borderRadius: 34,
      padding: 22,
      background:
        "linear-gradient(180deg, rgba(13,28,53,0.96) 0%, rgba(7,16,32,0.99) 100%)",
      backdropFilter: "blur(16px)",
      border: "1px solid rgba(96,165,250,.30)",
      boxShadow:
        "0 34px 100px rgba(0,0,0,.56), 0 0 0 1px rgba(56,189,248,.06), inset 0 1px 0 rgba(255,255,255,.06)",
      overflow: "hidden",
    },
    cardGlow: {
      position: "absolute",
      width: 300,
      height: 300,
      right: -150,
      top: -160,
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(49,130,255,.34), transparent 68%)",
      filter: "blur(8px)",
      pointerEvents: "none",
    },
    topRow: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      marginBottom: 12,
    },
    brandStack: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 10,
    },
    installUnderBadge: {
      background: "linear-gradient(90deg,#21d4fd,#22c55e)",
      color: "#03111f",
      fontWeight: 950,
      border: 0,
      borderRadius: 999,
      padding: "9px 14px",
      boxShadow: "0 12px 30px rgba(33,212,253,.22)",
      cursor: "pointer",
      lineHeight: 1,
    },
    brandBadge: {
      display: "inline-flex",
      alignItems: "center",
      fontSize: 12,
      fontWeight: 950,
      letterSpacing: 1.2,
      padding: "7px 12px",
      borderRadius: 999,
      background: "rgba(14,165,233,.18)",
      border: "1px solid rgba(14,165,233,.38)",
      color: "#dff4ff",
      textTransform: "uppercase",
    },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      fontSize: 12,
      fontWeight: 900,
      padding: "7px 10px",
      borderRadius: 999,
      background: "rgba(16,185,129,.10)",
      border: "1px solid rgba(16,185,129,.22)",
      color: "#bbf7d0",
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: "#22c55e",
      boxShadow: "0 0 14px rgba(34,197,94,.8)",
    },
    logoWrap: {
      position: "relative",
      zIndex: 1,
      width: 168,
      height: 168,
      margin: "4px auto 14px",
      borderRadius: "50%",
      display: "grid",
      placeItems: "start center",
      background:
        "radial-gradient(circle at 50% 45%, rgba(3,20,52,.98) 0%, rgba(2,10,30,1) 72%)",
      border: "2px solid rgba(0,170,255,.90)",
      boxShadow:
        "0 0 62px rgba(0,170,255,.35), inset 0 0 42px rgba(0,170,255,.13)",
      overflow: "hidden",
    },
    logoImg: {
      width: 150,
      height: 150,
      objectFit: "cover",
      borderRadius: "50%",
      filter: "drop-shadow(0 0 18px rgba(0,170,255,.42))",
    },
    layerText: {
      position: "relative",
      zIndex: 1,
      textAlign: "center",
      color: "#7dd3fc",
      letterSpacing: "0.42em",
      fontSize: 11,
      fontWeight: 950,
      textTransform: "uppercase",
      marginBottom: 10,
    },
    header: {
      position: "relative",
      zIndex: 1,
      textAlign: "center",
      marginBottom: 16,
    },
    title: {
      margin: 0,
      fontSize: "clamp(30px, 5vw, 42px)",
      lineHeight: 1,
      fontWeight: 950,
      letterSpacing: "-.05em",
      color: "#f8fafc",
    },
    subtitle: {
      margin: "10px auto 14px",
      maxWidth: 360,
      color: "rgba(226,232,240,.78)",
      lineHeight: 1.45,
      fontSize: 14,
    },
    workspaceCard: {
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      gap: 12,
      alignItems: "center",
      textAlign: "left",
      borderRadius: 22,
      padding: 14,
      background: "rgba(2,6,23,.32)",
      border: "1px solid rgba(125,211,252,.13)",
    },
    workspaceLabel: {
      fontSize: 10,
      letterSpacing: ".18em",
      textTransform: "uppercase",
      fontWeight: 900,
      color: "#7dd3fc",
      marginBottom: 5,
    },
    workspaceName: {
      fontSize: 14,
      fontWeight: 900,
      color: "#f8fafc",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    workspaceDivider: {
      width: 1,
      height: 36,
      background: "rgba(148,163,184,.20)",
    },
    modeBanner: {
      marginBottom: 14,
      borderRadius: 18,
      padding: "13px 14px",
      border: "1px solid rgba(16,185,129,.26)",
      background: "rgba(16,185,129,.10)",
    },
    modeBannerTitle: {
      fontSize: 13,
      fontWeight: 900,
      color: "#bbf7d0",
      textTransform: "uppercase",
      letterSpacing: ".04em",
    },
    modeBannerText: {
      marginTop: 6,
      fontSize: 13,
      lineHeight: 1.5,
      color: "#dcfce7",
    },
    installBanner: {
      position: "relative",
      zIndex: 1,
      marginBottom: 16,
      borderRadius: 22,
      padding: 15,
      background:
        "linear-gradient(135deg, rgba(14,165,233,.18), rgba(16,185,129,.10))",
      border: "1px solid rgba(125,211,252,.24)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)",
    },
    installBannerTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
    },
    installBannerTitle: {
      fontSize: 15,
      fontWeight: 950,
      color: "#f8fafc",
      marginBottom: 4,
    },
    installBannerText: {
      fontSize: 13,
      lineHeight: 1.45,
      color: "rgba(226,232,240,.9)",
    },
    installBannerClose: {
      border: 0,
      background: "transparent",
      color: "rgba(226,232,240,.85)",
      fontSize: 22,
      lineHeight: 1,
      cursor: "pointer",
      padding: 0,
    },
    installSteps: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 12,
    },
    stepChip: {
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 999,
      padding: "8px 10px",
      fontSize: 12,
      fontWeight: 800,
      background: "rgba(15,23,42,.55)",
      border: "1px solid rgba(255,255,255,.08)",
      color: "#e2e8f0",
    },
    installCta: {
      marginTop: 12,
      width: "100%",
      border: 0,
      borderRadius: 16,
      padding: "12px 14px",
      fontWeight: 950,
      background: "linear-gradient(90deg,#21d4fd,#3182ff,#18c37e)",
      color: "#04111d",
      cursor: "pointer",
      boxShadow: "0 14px 32px rgba(33,212,253,.18)",
    },
    installHint: {
      marginTop: 12,
      fontSize: 12,
      fontWeight: 800,
      color: "rgba(226,232,240,.86)",
    },
    form: {
      position: "relative",
      zIndex: 1,
      display: "grid",
      gap: 10,
      marginTop: 14,
    },
    label: {
      fontSize: 11,
      letterSpacing: ".18em",
      textTransform: "uppercase",
      color: "#7dd3fc",
      fontWeight: 950,
    },
    pin: {
      width: "100%",
      minHeight: 56,
      borderRadius: 18,
      border: "1px solid rgba(125,211,252,.20)",
      background: "rgba(2,6,23,.58)",
      color: "#e5e7eb",
      padding: "0 15px",
      fontSize: 18,
      outline: "none",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,.04)",
    },
    err: {
      background: "rgba(239,68,68,.16)",
      border: "1px solid rgba(239,68,68,.28)",
      color: "#fecaca",
      borderRadius: 14,
      padding: "10px 12px",
      fontSize: 13,
      whiteSpace: "pre-wrap",
    },
    btn: {
      minHeight: 56,
      border: 0,
      borderRadius: 18,
      padding: "13px 14px",
      fontWeight: 950,
      background: "linear-gradient(90deg,#21d4fd 0%,#3182ff 52%,#18c37e 100%)",
      color: "#001018",
      cursor: "pointer",
      boxShadow:
        "0 18px 42px rgba(33,212,253,.18), 0 10px 28px rgba(24,195,126,.12)",
    },
    btnDisabled: { opacity: 0.55, cursor: "not-allowed" },
    expText: { marginTop: 2, opacity: 0.85, fontSize: 12 },
    divider: {
      position: "relative",
      zIndex: 1,
      height: 1,
      margin: "22px 0",
      background:
        "linear-gradient(90deg, transparent, rgba(148,163,184,.34), transparent)",
    },
    manualPanel: {
      position: "relative",
      zIndex: 1,
      borderRadius: 22,
      padding: 14,
      background: "rgba(2,6,23,.28)",
      border: "1px solid rgba(125,211,252,.11)",
    },
    manualTitle: {
      fontSize: 15,
      fontWeight: 950,
      color: "#f8fafc",
      marginBottom: 5,
    },
    manualText: {
      fontSize: 12,
      lineHeight: 1.4,
      color: "rgba(226,232,240,.68)",
      marginBottom: 11,
    },
    manualInput: {
      width: "100%",
      minHeight: 50,
      padding: "0 13px",
      borderRadius: 16,
      marginBottom: 9,
      border: "1px solid rgba(125,211,252,.16)",
      background: "rgba(2,6,23,.52)",
      color: "#e5e7eb",
      outline: "none",
    },
    manualBtn: {
      width: "100%",
      minHeight: 50,
      borderRadius: 16,
      background: "linear-gradient(90deg, rgba(20,184,166,1), rgba(16,185,129,1))",
      color: "#052017",
      border: "none",
      fontWeight: 950,
      cursor: "pointer",
      boxShadow: "0 12px 30px rgba(16,185,129,.13)",
    },
    manualError: {
      color: "#fecaca",
      marginTop: 8,
      fontSize: 13,
      fontWeight: 800,
    },
    featureGrid: {
      position: "relative",
      zIndex: 1,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      marginTop: 12,
    },
    featureCard: {
      display: "grid",
      gap: 3,
      padding: "11px 12px",
      borderRadius: 18,
      background: "rgba(125,211,252,.07)",
      border: "1px solid rgba(125,211,252,.12)",
      color: "rgba(226,232,240,.72)",
      fontSize: 12,
      lineHeight: 1.35,
    },
    linkBtn: {
      position: "relative",
      zIndex: 1,
      width: "100%",
      marginTop: 12,
      padding: "12px 12px",
      borderRadius: 18,
      border: "1px solid rgba(255,255,255,.12)",
      background: "rgba(2,6,23,.34)",
      color: "#e5e7eb",
      fontWeight: 900,
      cursor: "pointer",
    },
    helpText: {
      position: "relative",
      zIndex: 1,
      display: "block",
      marginTop: 12,
      color: "rgba(226,232,240,.62)",
      lineHeight: 1.45,
      fontSize: 12,
    },
    modalWrap: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,.42)",
      backdropFilter: "blur(10px)",
      display: "grid",
      placeItems: "center",
      alignItems: "center",
      justifyItems: "center",
      zIndex: 50,
      padding: "max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))",
      overflowY: "auto",
    },
    modalBox: {
      width: "min(520px, 92vw)",
      borderRadius: 20,
      background: "rgba(15,23,42,.94)",
      border: "1px solid rgba(255,255,255,.10)",
      boxShadow: "0 18px 60px rgba(0,0,0,.45)",
      overflow: "hidden",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "14px 16px",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,.08)",
    },
    closeBtn: {
      border: 0,
      background: "transparent",
      color: "#e5e7eb",
      fontSize: 20,
      cursor: "pointer",
      lineHeight: 1,
    },
    modalBody: { padding: "14px 16px", whiteSpace: "pre-wrap", opacity: 0.95 },
    modalFooter: {
      padding: "14px 16px",
      borderTop: "1px solid rgba(255,255,255,.08)",
    },
  };
}

function splashStyles(cfg) {
  return {
    wrap: { position: "fixed", inset: 0, zIndex: 80, overflow: "hidden" },
    bg: {
      position: "absolute",
      inset: 0,
      background: cfg?.colors?.bg || "#0b1220",
    },
    cover: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      opacity: 0.98,
    },
  };
}
